var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography, } from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import RevenueAttributionApi from "../../../infrastructure/api/pages/revenue-attribution-api";
import UsersApi from "../../../infrastructure/api/pages/users-api";
import { canSaveSentBy, currentSenderLabel } from "./sentBySource";
// Changing who is credited for a proposal directly moves money between agents on the dashboards and
// leaderboards, so this is a deliberate two-step action (open dialog, pick, confirm) with the
// consequence spelled out — not an inline cell edit.
const ChangeSenderDialog = ({ open, row, onClose, onSaved }) => {
    var _a;
    const { t: translate } = useTranslation();
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [saving, setSaving] = useState(false);
    // Same eligible-user set as lead assignment (sales-agent roles, active only), so attribution can
    // never be handed to a deactivated account.
    useEffect(() => {
        if (!open)
            return;
        let cancelled = false;
        setLoadingUsers(true);
        UsersApi.getResponsiblePersons(false)
            .then((res) => {
            var _a;
            if (!cancelled)
                setUsers((_a = res.data) !== null && _a !== void 0 ? _a : []);
        })
            .catch(() => {
            if (!cancelled)
                toast.error(translate("Error while loading users."));
        })
            .finally(() => {
            if (!cancelled)
                setLoadingUsers(false);
        });
        return () => {
            cancelled = true;
        };
    }, [open, translate]);
    // Reset the selection to the row's current sender each time the dialog opens, so a previous
    // row's choice can never leak into a different contract.
    useEffect(() => {
        var _a;
        if (open)
            setSelectedUserId((_a = row === null || row === void 0 ? void 0 : row.sentBy) !== null && _a !== void 0 ? _a : null);
    }, [open, row]);
    const options = useMemo(() => users.map((u) => {
        var _a, _b, _c;
        return ({
            id: String(u.id),
            label: `${(_a = u.firstName) !== null && _a !== void 0 ? _a : ""} ${(_b = u.lastName) !== null && _b !== void 0 ? _b : ""}`.trim() || String((_c = u.email) !== null && _c !== void 0 ? _c : "-"),
        });
    }), [users]);
    const selectedOption = (_a = options.find((o) => o.id === selectedUserId)) !== null && _a !== void 0 ? _a : null;
    const canSave = canSaveSentBy(row, selectedUserId, saving);
    const handleSave = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        if (!row || !selectedUserId)
            return;
        setSaving(true);
        try {
            yield RevenueAttributionApi.updateSentBy({
                contractId: row.contractId,
                sentBy: selectedUserId,
                // Lets the server reject with 409 instead of silently overwriting a concurrent correction.
                expectedCurrentSentBy: row.sentBy,
            });
            toast.success(translate("Revenue attribution updated."));
            onSaved();
            onClose();
        }
        catch (err) {
            if (((_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.status) === 409) {
                toast.error(translate("This contract was changed by someone else. Reloading the latest values."));
                // Refresh rather than leave stale values on screen inviting a repeat of the same lost write.
                onSaved();
                onClose();
            }
            else {
                toast.error(((_c = (_b = err === null || err === void 0 ? void 0 : err.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.error) || translate("Error while updating revenue attribution."));
            }
        }
        finally {
            setSaving(false);
        }
    });
    return (_jsxs(Dialog, { open: open, onClose: !saving ? onClose : undefined, maxWidth: "xs", fullWidth: true, slotProps: {
            paper: { "data-testid": "change-sender-dialog" }
        }, children: [_jsx(DialogTitle, { sx: { fontSize: "24px", fontWeight: 500 }, children: translate("Change revenue attribution") }), _jsxs(DialogContent, { sx: { paddingBottom: 0 }, children: [_jsxs(Typography, { variant: "body2", sx: {
                            color: "text.secondary",
                            mb: 2
                        }, children: [translate("Offer"), " ", _jsx(Box, { component: "span", sx: {
                                    fontWeight: 600,
                                    color: "text.primary"
                                }, children: (row === null || row === void 0 ? void 0 : row.contractNumber) || "-" }), " · ", translate("Lead"), " #", row === null || row === void 0 ? void 0 : row.leadNumber, (row === null || row === void 0 ? void 0 : row.companyName) ? ` · ${row.companyName}` : ""] }), _jsxs(Typography, { variant: "body2", sx: {
                            color: "text.secondary",
                            mb: 2
                        }, children: [translate("Currently credited to"), " ", _jsx(Box, { component: "span", sx: {
                                    fontWeight: 600,
                                    color: "text.primary"
                                }, children: currentSenderLabel(row) })] }), _jsx(Autocomplete, { options: options, value: selectedOption, loading: loadingUsers, onChange: (_, value) => { var _a; return setSelectedUserId((_a = value === null || value === void 0 ? void 0 : value.id) !== null && _a !== void 0 ? _a : null); }, isOptionEqualToValue: (option, value) => option.id === value.id, renderInput: (params) => (_jsx(TextField, Object.assign({}, params, { label: translate("Credit revenue to"), slotProps: Object.assign(Object.assign({}, params.slotProps), { input: Object.assign(Object.assign({}, params.slotProps.input), { endAdornment: (_jsxs(_Fragment, { children: [loadingUsers ? _jsx(CircularProgress, { size: 16 }) : null, params.slotProps.input.endAdornment] })) }), htmlInput: Object.assign(Object.assign({}, params.slotProps.htmlInput), { "data-testid": "sender-select" }) }) }))) }), _jsx(Typography, { variant: "caption", sx: {
                            color: "text.secondary",
                            display: "block",
                            mt: 2
                        }, children: translate("This immediately changes the revenue and leaderboard figures for both agents. The change is recorded in the lead history.") })] }), _jsxs(DialogActions, { sx: { px: 3, pb: 2.5, pt: 1.5 }, children: [_jsx(Button, { variant: "outlined", onClick: onClose, disabled: saving, sx: { borderRadius: "8px" }, children: translate("Cancel") }), _jsx(Button, { variant: "contained", "data-testid": "sender-save", onClick: handleSave, disabled: !canSave, sx: { borderRadius: "8px" }, children: saving ? _jsx(CircularProgress, { size: 20 }) : translate("Save") })] })] }));
};
export default ChangeSenderDialog;
